/* ============================================================
   Cart Drawer v2 — Phase 1a

   Adds on top of Phase 0:
   - Protocol detection engine (5 named protocols, pair-of-individuals
     OR bundle line item)
   - Two-tier pricing math (Option 3: real per-product single_sub_discount
     read from selling_plan_allocation + PROTOCOL_UPLIFT=0.07)
   - Decorated line items with display_price, role tags, savings
   - Auto-apply Protocol_pricing discount code via cart attribute
     when a protocol is formed
   - Keystone partner mapping (HB-alone → Microbiome, etc.)
   - Quantity change + remove handlers
   - Bridge module recommendation (host-protocol → bridge product)
   ============================================================ */

(function () {
  // ----------------------------------------------------------
  // CONFIG — protocol architecture
  // ----------------------------------------------------------

  // Locked from the strategy doc + verified against Shopify (handles
  // exist in the catalog as of 2026-05-25). Bundle handles fold into
  // the same protocol — a bundle line item counts as "protocol formed"
  // without needing both individual products separately in the cart.
  const DEFINED_PROTOCOLS = [
    {
      slug: 'hormone',
      name: 'The Hormone Protocol',
      // Benefit-led frame (customer-recognizable outcome, not architecture).
      // Per founder direction: lead with what she'll FEEL, not what the
      // protocol IS structurally.
      frame: 'Feel like yourself again.',
      eyebrow: 'Complete your protocol',
      benefit_subhead: 'Feel like yourself again.',
      cta_verb: 'Steady my transition',
      mirror_line: "Built for women who'd rather meet the transition whole than treat it piece by piece.",
      individuals: ['hormone-balance', 'menopause-essentials'],
      bundle_handles: ['bundle-40plus-essentials'],
      // Keystone teach (state 01b): one-line benefit intro + 3 scannable
      // checkmark bullets. Bullets surface customer-recognizable outcomes
      // at a glance — beats prose at the flash-decision keystone moment.
      // Deeper mechanism teaching lives in how_teach (How this works).
      keystone_teach: {
        intro: "When your hormones shift, the rest of you shouldn't.",
        benefits: [
          'Ease hot flashes + night sweats',
          'Steady mood + sleep through the transition',
          'Daily foundation: active B vitamins, magnesium, zinc'
        ]
      },
      // "How this works" expandable — intro + 4 bullets. Deeper layer
      // than keystone (customer has clicked to expand), so bullets get
      // a touch more detail.
      how_teach: {
        intro: "Menopause is two things happening at once. Your hormones shift, and your body still needs the daily resources to use them.",
        benefits: [
          'Peri/Meno Essentials: shaped for the receptors your brain reaches for as estrogen shifts',
          'Hormone Balance: active forms of B vitamins, magnesium, and zinc your cells use every day',
          'The methylated forms that work for the 40% of women who don’t fully convert the standard ones',
          'Both halves of menopause, addressed at the root'
        ]
      },
      how_closer: "Your hormones change. Your foundation doesn't.",
      // Cross-protocol bridge (Pre+Probiotic for Hormone Protocol customers)
      bridge_product_handle: 'pre-probiotic-for-women',
      bridge_eyebrow_layer: 'the gut',
      bridge_frame: 'Better moods. Less bloat. From the gut up.',
      bridge_teach: {
        intro: "The estrobolome shifts as estrogen does.",
        benefits: [
          'Gut bacteria that process estrogen',
          'Probiotic strains for mood + vaginal health',
          'Standardized ginger for digestion'
        ]
      }
    },
    {
      slug: 'cycle',
      name: 'The Cycle Protocol',
      frame: 'Easier cycles. Every month.',
      eyebrow: 'Complete your protocol',
      benefit_subhead: 'Easier cycles. Every month.',
      cta_verb: 'Steady my cycles',
      mirror_line: "Built for women who'd rather change the climate than outlast every storm.",
      individuals: ['hormone-balance', 'pms-relief-capsule'],
      bundle_handles: ['bundle-cycle-self-care-set'],
      keystone_teach: {
        intro: "Predictable cycles take both: the days and the months.",
        benefits: [
          'Relief when PMS hits: cramps, mood, bloat',
          'Steadier mood through every cycle',
          'Daily foundation for the climate underneath'
        ]
      },
      how_teach: {
        intro: "Your cycle runs on two timelines: the months underneath, and the days when symptoms hit.",
        benefits: [
          'PMS Relief Capsule: targeted relief for cramps, mood, bloating',
          'Hormone Balance: the daily climate every cycle depends on',
          'Active forms of B vitamins, magnesium, and zinc your hormones use every day of the month',
          'Built to change the climate, not just react to the weather'
        ]
      },
      how_closer: "Change the climate, not just the weather.",
      bridge_product_handle: 'pre-probiotic-for-women',
      bridge_eyebrow_layer: 'the gut',
      bridge_frame: 'Your cycle starts in your gut too.',
      bridge_teach: {
        intro: "The estrobolome metabolizes the estrogen your cycle hinges on.",
        benefits: [
          'Microbial layer underneath every cycle',
          'Less PMS bloat + digestive discomfort',
          'Probiotic strains for vaginal health'
        ]
      }
    },
    {
      slug: 'microbiome',
      name: 'The Microbiome Protocol',
      frame: 'Better moods. From the gut up.',
      eyebrow: 'Complete your protocol',
      benefit_subhead: 'Better moods. From the gut up.',
      cta_verb: 'Brighten my days',
      mirror_line: "Built for women who'd rather close the loop than choose between her gut and her mood.",
      individuals: ['hormone-balance', 'pre-probiotic-for-women'],
      bundle_handles: ['hormonal-gut-balance'],
      keystone_teach: {
        intro: "Mood, bloat, hormones. They all run through your gut.",
        benefits: [
          'Steadier moods (estrobolome supports estrogen)',
          'Less bloat, easier digestion',
          'Daily hormone foundation underneath'
        ]
      },
      how_teach: {
        intro: "The gut-hormone loop runs both directions. Your gut produces what your hormones depend on.",
        benefits: [
          'Estrobolome: gut bacteria that metabolize estrogen',
          'Probiotic strains for vaginal health',
          'Ginger for the enteric nervous system (mood + digestion)',
          'Hormone Balance: the foundation that lets your body use what the loop produces'
        ]
      },
      how_closer: "Closed loops compound. Open ones leak.",
      bridge_product_handle: 'stress-less-gummy',
      bridge_eyebrow_layer: 'stress',
      bridge_frame: 'Steadier moods. Calmer stress response.',
      bridge_teach: {
        intro: "The gut-hormone loop runs through the HPA axis.",
        benefits: [
          'Modulates cortisol (the stress hormone)',
          'Supports the HPA axis underneath the loop',
          'Steadier mood + energy through the day'
        ]
      }
    },
    {
      slug: 'urinary',
      name: 'The Urinary Protocol',
      frame: 'Urinary comfort, every day.',
      eyebrow: 'Complete your protocol',
      benefit_subhead: 'Urinary comfort, every day.',
      cta_verb: 'Look after my bladder',
      mirror_line: "Built for women who'd rather invest in the urinary environment than wait for the next episode.",
      individuals: ['hormone-balance', 'urinary-tract-cleanse-protect'],
      bundle_handles: ['daily-defense'],
      keystone_teach: {
        intro: "Urinary changes start before symptoms do.",
        benefits: [
          'Daily urinary tract support',
          'Cranberry at the biofilm layer (3x absorbed)',
          'Hormonal foundation underneath'
        ]
      },
      how_teach: {
        intro: "The urinary tract changes as estrogen declines. Tissue integrity shifts, the local microbiome shifts, and what used to be resilient quietly stops being so.",
        benefits: [
          'UTCP: phytosome cranberry, 3x better absorbed',
          'Interferes with bacterial adhesion at the biofilm layer',
          'Hormone Balance: foundation for the systems estrogen used to coordinate',
          'Daily work, at the root'
        ]
      },
      how_closer: "Stay ahead of changes you can't see yet.",
      bridge_product_handle: 'pre-probiotic-for-women',
      bridge_eyebrow_layer: 'the microbiome',
      bridge_frame: 'Your urinary comfort starts in your gut.',
      bridge_teach: {
        intro: "The urinary tract is downstream of the gut and vaginal microbiome.",
        benefits: [
          'L. acidophilus keeps the vaginal environment acidic',
          'Gut microbiome feeds urinary defenses',
          'The upstream layer your urinary tract depends on'
        ]
      }
    },
    {
      slug: 'brp',
      name: 'The Bladder Resilience Protocol',
      frame: 'Break the loop.',
      eyebrow: 'Complete your protocol',
      benefit_subhead: 'Break the loop.',
      cta_verb: 'Build my defense',
      mirror_line: "Built for women who'd rather break the loop than chase it.",
      individuals: ['urinary-tract-cleanse-protect', 'pre-probiotic-for-women'],
      bundle_handles: ['ph-gut-balance'],
      keystone_teach: {
        intro: "The bacteria don't come from the bladder.",
        benefits: [
          'Bladder layer: anti-biofilm cranberry',
          'Upstream layer: gut + vaginal microbiome',
          'Defense at the source, not just the symptom'
        ]
      },
      how_teach: {
        intro: "Recurrence happens because most approaches address the symptom, not the source.",
        benefits: [
          'UTCP at the bladder: phytosome cranberry (anti-biofilm)',
          'Pre+Probiotic upstream: gut + vaginal microbiome',
          'L. acidophilus suppresses pathogenic E. coli at the source',
          'Two layers, addressed at the root'
        ]
      },
      how_closer: "Break the loop, not just the episode.",
      bridge_product_handle: 'stress-less-gummy',
      bridge_eyebrow_layer: 'stress',
      bridge_frame: 'Stress fuels recurrence. Address the layer.',
      bridge_teach: {
        intro: "Cortisol affects every layer of bladder defense.",
        benefits: [
          'Affects the bladder lining directly',
          'Modulates the gut + vaginal microbiome',
          'The layer underneath your whole defense'
        ]
      }
    }
  ];

  // Protocol uplift on top of single-sub discount. Locked from the
  // handoff brief Section 2.2 (Option 3).
  const PROTOCOL_UPLIFT = 0.07;

  // Single-product keystone partner mapping. Answers: "She has only X
  // in her cart — which protocol does the completion module surface?"
  // Per the founder's answer: HB-alone defaults to Microbiome.
  const KEYSTONE_PARTNERS = {
    'hormone-balance':                  { protocol: 'microbiome', partner: 'pre-probiotic-for-women' },
    'menopause-essentials':              { protocol: 'hormone',    partner: 'hormone-balance' },
    'urinary-tract-cleanse-protect':     { protocol: 'brp',        partner: 'pre-probiotic-for-women' },
    'pre-probiotic-for-women':           { protocol: 'microbiome', partner: 'hormone-balance' },
    'pms-relief-capsule':                { protocol: 'cycle',      partner: 'hormone-balance' },
    'pms-relief-gummy':                  { protocol: 'cycle',      partner: 'hormone-balance' }
  };

  // The discount code that auto-applies when ANY protocol is formed.
  // Configured in Shopify admin as a 7%-off-subscriptions code with
  // "Combines with product / order / shipping discounts" enabled so it
  // stacks naturally alongside customer-entered promo codes.
  // Must stay in sync with PROTOCOL_DISCOUNT_CODE in
  // extensions/protocol-pricing-guard/src/cart_validations_generate_run.ts.
  const PROTOCOL_DISCOUNT_CODE = 'PROTOCOL';
  // Single-underscore prefix marks the attribute as "private" (hidden from
  // the customer-facing checkout summary while still readable via API).
  // Double-underscore variants are silently dropped by Shopify between
  // POST and persistent storage — verified empirically 2026-05-25.
  const PROTOCOL_CART_ATTR = '_protocol_pricing_active';

  // Free shipping qualification rules (per founder):
  // - Any subscription item in cart → ships free
  // - Cart subtotal $60+ (all-OTP) → ships free
  // - Otherwise → progress + remaining amount surfaced
  const FREE_SHIP_THRESHOLD_CENTS = 6000;

  // Protocol breakage tracking — sessionStorage keys.
  // When a protocol that WAS formed is no longer formed, the keystone
  // module overrides its partner suggestion to point at the missing
  // partner of the broken protocol (instead of the default keystone
  // partner mapping).
  const RECENT_PROTOCOL_KEY = 'cv2_recent_protocols';

  // ----------------------------------------------------------
  // PURE FUNCTIONS — detection + pricing
  // ----------------------------------------------------------

  /**
   * Is this line item a subscription? Anything with a selling_plan_allocation
   * is a sub, per the founder's confirmation.
   */
  function isSubscription(item) {
    return !!(item && item.selling_plan_allocation);
  }

  /**
   * Return the OTP (regular) per-unit price for a line item.
   *
   * For OTP items: item.original_price (== item.price).
   * For sub items: selling_plan_allocation.compare_at_price — Shopify's
   *   item.original_price ALREADY has the selling plan discount baked in
   *   for subs, so we have to read the variant's compare-at from the
   *   selling_plan_allocation.
   */
  function getOtpPrice(item) {
    if (isSubscription(item) && item.selling_plan_allocation) {
      return item.selling_plan_allocation.compare_at_price || item.original_price || item.price || 0;
    }
    return item.original_price || item.price || 0;
  }

  /**
   * Return the sub per-unit price (post-selling-plan) for a line item.
   * For OTP items: item.price.
   * For sub items: selling_plan_allocation.per_delivery_price (or item.price).
   */
  function getSubPrice(item) {
    if (isSubscription(item) && item.selling_plan_allocation) {
      return item.selling_plan_allocation.per_delivery_price || item.price || 0;
    }
    return item.price || 0;
  }

  /**
   * Compute the real per-product single-sub discount ratio from the
   * line item's selling plan. Returns 0 for OTP items.
   *
   * single_sub_discount = (otp_price - sub_price) / otp_price
   */
  function getSingleSubDiscount(item) {
    if (!isSubscription(item)) return 0;
    var otp = getOtpPrice(item);
    var sub = getSubPrice(item);
    if (otp <= 0) return 0;
    return Math.max(0, (otp - sub) / otp);
  }

  /**
   * Bundle product handles → protocol slug map, built once.
   */
  var BUNDLE_TO_PROTOCOL = {};
  DEFINED_PROTOCOLS.forEach(function (p) {
    (p.bundle_handles || []).forEach(function (h) {
      BUNDLE_TO_PROTOCOL[h] = p.slug;
    });
  });

  /**
   * Detect formed protocols from a cart's line items.
   *
   * Returns:
   * {
   *   formed: [{ protocol, formation_timestamp, source: 'pair'|'bundle' }],
   *   primary: <protocol object | null>,
   *   personalizations: <line_items not part of the primary>,
   *   has_otp_mix: <bool — true if cart has both OTP and sub items>,
   *   all_subs: <bool — true if every item is a subscription>,
   *   any_subs: <bool>,
   *   subscription_items: <items[]>
   * }
   */
  function detectProtocols(items) {
    var subs = items.filter(isSubscription);
    var otp = items.filter(function (i) { return !isSubscription(i); });

    var formed = [];

    // 1. Bundle detection: any single line item whose handle maps to a protocol.
    items.forEach(function (item, idx) {
      var slug = BUNDLE_TO_PROTOCOL[item.handle];
      if (!slug) return;
      var protocol = DEFINED_PROTOCOLS.find(function (p) { return p.slug === slug; });
      if (!protocol) return;
      formed.push({
        protocol: protocol,
        formation_timestamp: idx, // entry-order proxy (items rendered reverse-chronologically)
        source: 'bundle',
        bundle_item: item,
        constituent_items: [item]
      });
    });

    // 2. Pair detection: subscription items only (OTP doesn't qualify for protocol tier).
    var subHandles = {};
    subs.forEach(function (item, idx) {
      subHandles[item.handle] = subHandles[item.handle] || { item: item, idx: idx };
    });

    DEFINED_PROTOCOLS.forEach(function (protocol) {
      var a = subHandles[protocol.individuals[0]];
      var b = subHandles[protocol.individuals[1]];
      if (a && b) {
        // Skip if this protocol is already formed via bundle — bundle wins
        var dup = formed.find(function (f) { return f.protocol.slug === protocol.slug; });
        if (dup) return;
        formed.push({
          protocol: protocol,
          formation_timestamp: Math.max(a.idx, b.idx),
          source: 'pair',
          constituent_items: [a.item, b.item]
        });
      }
    });

    // Primary = earliest-formed protocol (oldest timestamp wins).
    formed.sort(function (x, y) { return x.formation_timestamp - y.formation_timestamp; });
    var primary = formed.length > 0 ? formed[0] : null;

    var personalizations = [];
    if (primary) {
      var primaryItemKeys = (primary.constituent_items || []).map(function (i) { return i.key; });
      personalizations = items.filter(function (i) {
        return primaryItemKeys.indexOf(i.key) === -1;
      });
    }

    var detection = {
      formed: formed,
      primary: primary,
      personalizations: personalizations,
      has_otp_mix: subs.length > 0 && otp.length > 0,
      all_subs: items.length > 0 && otp.length === 0,
      any_subs: subs.length > 0,
      subscription_items: subs
    };
    detection.potential_protocol = detectPotentialProtocol(items, detection);
    return detection;
  }

  /**
   * Detect "near-protocol" state: cart has one sub + one OTP that together
   * would form a defined protocol if the OTP were converted to a sub.
   * Drives the protocol-formation framing on the per-line upgrade pill.
   *
   * Returns null if no such pair exists, OR if a protocol is already formed
   * (the customer who already has a complete protocol doesn't need this prompt).
   */
  function detectPotentialProtocol(items, detection) {
    if (detection.formed.length > 0) return null;
    var subs = items.filter(isSubscription);
    var otp = items.filter(function (i) { return !isSubscription(i); });
    if (subs.length === 0 || otp.length === 0) return null;

    for (var i = 0; i < DEFINED_PROTOCOLS.length; i++) {
      var protocol = DEFINED_PROTOCOLS[i];
      var aHandle = protocol.individuals[0];
      var bHandle = protocol.individuals[1];

      var aSub = subs.find(function (s) { return s.handle === aHandle; });
      var aOtp = otp.find(function (o) { return o.handle === aHandle; });
      var bSub = subs.find(function (s) { return s.handle === bHandle; });
      var bOtp = otp.find(function (o) { return o.handle === bHandle; });

      if (aSub && bOtp) {
        return { protocol: protocol, otp_item: bOtp, sub_item: aSub, otp_handle: bHandle };
      }
      if (aOtp && bSub) {
        return { protocol: protocol, otp_item: aOtp, sub_item: bSub, otp_handle: aHandle };
      }
    }
    return null;
  }

  /**
   * Compute keystone state — applies when there's one subscription item
   * in the cart that doesn't yet form a protocol. Returns the recommended
   * partner + protocol it would complete.
   *
   * Returns null if conditions don't apply.
   */
  function detectKeystone(items, detection) {
    if (detection.formed.length > 0) return null;       // already complete
    if (detection.subscription_items.length !== 1) return null; // need exactly one sub
    var item = detection.subscription_items[0];
    var mapping = KEYSTONE_PARTNERS[item.handle];
    if (!mapping) return null;

    var protocol = DEFINED_PROTOCOLS.find(function (p) { return p.slug === mapping.protocol; });
    if (!protocol) return null;

    return {
      held_item: item,
      partner_handle: mapping.partner,
      protocol: protocol
    };
  }

  /**
   * Decorate a line item with display pricing + role tags based on detection.
   *
   * Mutates a shallow copy — never the original.
   */
  function decorateLineItem(item, detection) {
    var sub_discount = getSingleSubDiscount(item);
    var is_sub = isSubscription(item);

    // Protocol-tier pricing: per strategy doc Section 6 Step 2, when ANY
    // named protocol is formed in subscription items, ALL subscription items
    // get the protocol tier (single_sub_discount + PROTOCOL_UPLIFT). Pair
    // members AND personalizations alike. The role tags differ (Foundation
    // is only on the canonical foundation product) but the pricing math
    // is uniform across the cart once protocol_tier_active flips true.
    //
    // Bundle items keep their own bundle price (no uplift on top). They
    // form a protocol on their own, but they don't get re-priced — the
    // bundle's price already reflects a discount.
    var is_bundle_line = !!BUNDLE_TO_PROTOCOL[item.handle];
    var protocol_tier_active = !!detection.primary;
    var membership_role = null; // 'foundation' | 'personalization' | 'bundle' | null

    if (detection.primary) {
      var constituents = detection.primary.constituent_items || [];
      var member = constituents.find(function (ci) { return ci.key === item.key; });
      if (member) {
        if (detection.primary.source === 'bundle') {
          membership_role = 'bundle';
        } else {
          // 'foundation' if this is the canonical foundation product
          // (first product in protocol.individuals — HB for the four
          // HB-anchored protocols, UTCP for BRP)
          var foundation_handle = detection.primary.protocol.individuals[0];
          membership_role = (item.handle === foundation_handle) ? 'foundation' : 'personalization';
        }
      } else {
        // Item is in the cart but not in the primary protocol's constituents.
        // It's a personalization on the cart's protocol stack.
        membership_role = 'personalization';
      }
    }

    var protocol_tier_applies = protocol_tier_active && is_sub && !is_bundle_line;

    var display_price; // per-unit
    var compare_price; // per-unit OTP for strike
    if (is_sub) {
      compare_price = getOtpPrice(item);
      var sub_price = getSubPrice(item);
      if (protocol_tier_applies) {
        // MULTIPLICATIVE math — matches how Shopify's discount engine
        // actually applies stacked discounts (selling plan first, then
        // discount code). Compound, not additive:
        //   display = sub_price × (1 - PROTOCOL_UPLIFT)
        //           = OTP × (1 - sub_discount) × (1 - 0.07)
        // The "Save N%" tag below recomputes the effective % from the
        // actual ratio so it stays honest per-product (sub discounts
        // vary by product/pack-size).
        display_price = Math.round(sub_price * (1 - PROTOCOL_UPLIFT));
      } else {
        // Subscription tier only
        display_price = sub_price;
      }
    } else {
      compare_price = item.original_price;
      display_price = item.price;
    }

    // Build role tags. Savings % derives from the ACTUAL display vs
    // compare price ratio — honest per-product since sub discounts
    // vary by product and pack size, and compound math doesn't
    // collapse to a single tidy global number.
    var tags = [];
    if (membership_role === 'foundation') tags.push({ kind: 'foundation', label: 'Foundation' });
    if (protocol_tier_applies && compare_price > 0) {
      var actual_savings_ratio = 1 - (display_price / compare_price);
      var savings_pct = Math.round(actual_savings_ratio * 100);
      tags.push({ kind: 'protocol_price', label: 'Protocol price · Save ' + savings_pct + '%' });
    } else if (is_sub && compare_price > 0) {
      var sub_ratio = 1 - (display_price / compare_price);
      var sub_pct = Math.round(sub_ratio * 100);
      if (sub_pct > 0) {
        tags.push({ kind: 'sub_save', label: 'Save ' + sub_pct + '%' });
      }
    }

    // Sub+OTP edge case: if THIS item is an OTP that would complete a
    // protocol when converted to sub, surface the protocol-formation framing
    // on the per-line upgrade pill.
    var potential_protocol_for_me = null;
    if (!is_sub && detection.potential_protocol && detection.potential_protocol.otp_handle === item.handle) {
      potential_protocol_for_me = detection.potential_protocol.protocol;
    }

    return Object.assign({}, item, {
      _decorated: true,
      _is_sub: is_sub,
      _is_bundle_line: is_bundle_line,
      _sub_discount: sub_discount,
      _in_protocol: protocol_tier_active,
      _membership_role: membership_role,
      _protocol_tier_applies: protocol_tier_applies,
      _display_price: display_price,
      _compare_price: compare_price,
      _display_line_price: display_price * item.quantity,
      _tags: tags,
      _potential_protocol: potential_protocol_for_me
    });
  }

  /**
   * Compute totals from decorated items + optional customer-applied
   * promo codes (everything except our PROTOCOL code, which is already
   * baked into per-line display prices via the protocol-tier math).
   */
  function computeTotals(decorated, detection, customer_codes) {
    var subtotal = 0;                  // sum of compare (OTP) prices × qty
    var subscription_savings = 0;      // OTP→sub portion only
    var protocol_savings = 0;          // protocol uplift portion only
    var total = 0;                     // sum of display prices × qty

    // Per-month protocol savings (normalized across pack sizes). For a
    // 1-pack monthly cart, per-delivery savings === per-month savings.
    // For a 3-pack quarterly cart, per-delivery savings ÷ 3 = per-month
    // equivalent. Heuristic: variant_title "3 Pack" → 3 months. Drives
    // the annual savings reframe honestly across cadences.
    var monthly_protocol_savings = 0;

    decorated.forEach(function (item) {
      var qty = item.quantity || 1;
      var compare = (item._compare_price || 0) * qty;
      var display = (item._display_price || 0) * qty;

      subtotal += compare;
      total += display;

      if (item._is_sub) {
        // Use the canonical sub-only price from the selling plan allocation
        // (not item.price, which is identical and would still work but is
        // less explicit).
        var sub_only_price = getSubPrice(item) * qty;
        var raw_sub_savings = Math.max(0, compare - sub_only_price);
        subscription_savings += raw_sub_savings;
        if (item._protocol_tier_applies) {
          var raw_protocol_savings = Math.max(0, sub_only_price - display);
          protocol_savings += raw_protocol_savings;
          var vt = (item.variant_title || '').toLowerCase();
          var months_per_delivery = vt.indexOf('3 pack') !== -1 ? 3
                                    : vt.indexOf('2 pack') !== -1 ? 2
                                    : 1;
          monthly_protocol_savings += raw_protocol_savings / months_per_delivery;
        }
      }
    });

    var protocol_formed = !!detection.primary;
    var monthly_savings = protocol_formed ? monthly_protocol_savings : 0;
    var annual_savings  = monthly_savings * 12;

    // Subtract customer-applied cart-level codes so "Today" matches what
    // Shopify actually charges at checkout. The promo code rows show
    // each code's amount separately above the Today line.
    var customer_codes_total = 0;
    if (customer_codes && customer_codes.length) {
      for (var i = 0; i < customer_codes.length; i++) {
        customer_codes_total += customer_codes[i].amount_cents || 0;
      }
    }
    total = Math.max(0, total - customer_codes_total);

    return {
      subtotal: subtotal,
      subscription_savings: subscription_savings,
      protocol_savings: protocol_savings,
      customer_codes_total: customer_codes_total,
      total: total,
      protocol_formed: protocol_formed,
      monthly_savings: monthly_savings,
      annual_savings: annual_savings
    };
  }

  // ----------------------------------------------------------
  // INTERNAL TEST HARNESS — runs in dev only, console output
  // ----------------------------------------------------------
  function _runSelfTests() {
    if (typeof window === 'undefined') return;
    if (!window.location.search.includes('cartv2test=1')) return;

    var assert = function (cond, msg) {
      if (cond) console.log('%c[v2 self-test] PASS', 'color:green', msg);
      else      console.error('[v2 self-test] FAIL', msg);
    };

    // Fixture: HB sub + MHS sub
    // NOTE: for subs, Shopify sets item.original_price = sub price (NOT OTP);
    // OTP price lives at selling_plan_allocation.compare_at_price.
    var fx_hormone_pair = [
      { key: 'k1', handle: 'hormone-balance', quantity: 1, price: 3144, original_price: 3144, selling_plan_allocation: { per_delivery_price: 3144, compare_at_price: 3699 } },
      { key: 'k2', handle: 'menopause-essentials', quantity: 1, price: 3144, original_price: 3144, selling_plan_allocation: { per_delivery_price: 3144, compare_at_price: 3699 } }
    ];
    var d = detectProtocols(fx_hormone_pair);
    assert(d.formed.length === 1, 'pair: one protocol formed');
    assert(d.primary && d.primary.protocol.slug === 'hormone', 'pair: primary is hormone');
    var deco = fx_hormone_pair.map(function (i) { return decorateLineItem(i, d); });
    assert(deco[0]._protocol_tier_applies, 'pair: HB gets protocol tier');
    assert(deco[1]._protocol_tier_applies, 'pair: MHS gets protocol tier');
    // Multiplicative math: 36.99 × 0.85 × 0.93 = 29.2404 → 2924
    assert(deco[0]._display_price === 2924, 'pair: HB display price = 29.24 (multiplicative) — got ' + (deco[0]._display_price / 100));
    var t = computeTotals(deco, d);
    // Protocol savings per item: 31.44 × 0.07 = 2.2008 → 220 (rounded display means 31.44 - 29.24 = 2.20)
    // Two items × $2.20 = $4.40
    assert(t.protocol_savings === 440, 'pair: protocol_savings = 4.40 — got ' + (t.protocol_savings / 100));

    // Fixture: HB sub alone (keystone)
    var fx_keystone = [
      { key: 'k1', handle: 'hormone-balance', quantity: 1, price: 3144, original_price: 3699, selling_plan_allocation: { per_delivery_price: 3144, compare_at_price: 3699 } }
    ];
    var d2 = detectProtocols(fx_keystone);
    assert(d2.formed.length === 0, 'keystone: no protocol formed');
    var ks = detectKeystone(fx_keystone, d2);
    assert(ks && ks.protocol.slug === 'microbiome', 'keystone: HB-alone → Microbiome');
    assert(ks && ks.partner_handle === 'pre-probiotic-for-women', 'keystone: partner is Pre+Probiotic');

    // Fixture: Bundle line item
    var fx_bundle = [
      { key: 'k1', handle: 'bundle-40plus-essentials', quantity: 1, price: 5949, original_price: 6999, selling_plan_allocation: { per_delivery_price: 5949, compare_at_price: 6999 } }
    ];
    var d3 = detectProtocols(fx_bundle);
    assert(d3.formed.length === 1 && d3.formed[0].source === 'bundle', 'bundle: detected as protocol');
    assert(d3.primary.protocol.slug === 'hormone', 'bundle: maps to hormone protocol');

    // Fixture: OTP only (no protocol tier)
    var fx_otp = [
      { key: 'k1', handle: 'hormone-balance', quantity: 1, price: 3699, original_price: 3699 }
    ];
    var d4 = detectProtocols(fx_otp);
    assert(d4.formed.length === 0, 'otp: no protocol');
    assert(d4.has_otp_mix === false, 'otp: not a mix when no subs');

    console.log('[v2 self-test] complete.');
  }

  // ----------------------------------------------------------
  // EXPOSE for Alpine + debugging
  // ----------------------------------------------------------
  window.__CartV2 = {
    DEFINED_PROTOCOLS: DEFINED_PROTOCOLS,
    PROTOCOL_UPLIFT: PROTOCOL_UPLIFT,
    PROTOCOL_DISCOUNT_CODE: PROTOCOL_DISCOUNT_CODE,
    PROTOCOL_CART_ATTR: PROTOCOL_CART_ATTR,
    KEYSTONE_PARTNERS: KEYSTONE_PARTNERS,
    BUNDLE_TO_PROTOCOL: BUNDLE_TO_PROTOCOL,
    isSubscription: isSubscription,
    getSingleSubDiscount: getSingleSubDiscount,
    detectProtocols: detectProtocols,
    detectKeystone: detectKeystone,
    decorateLineItem: decorateLineItem,
    computeTotals: computeTotals
  };

  // ----------------------------------------------------------
  // ALPINE COMPONENT
  // ----------------------------------------------------------
  document.addEventListener('alpine:init', () => {
    Alpine.data('CartDrawerV2', () => ({
      /* ----- raw cart state ----- */
      cart: {
        items: [],
        item_count: 0,
        total_price: 0,
        original_total_price: 0,
        total_discount: 0,
        attributes: {}
      },
      flags: {
        open: false,
        empty: true,
        preview_mode: false,
        protocol_active: false,
        how_open: false
      },
      // Cache hydrated /products/X.js for product images, ingredient
      // names, bridge product card data. Shared with v1 via window.__semaine_product_cache.
      product_cache: {},
      // Bridge product (lazy-fetched when complete-protocol renders)
      bridge_product: null,
      // Foundation product (Hormone Balance) — lazy-fetched on init so the
      // empty-state foundation card uses the real product image + sub price
      // instead of the bottle silhouette + hardcoded text.
      foundation_product: null,
      // Memoization cache for hot accessors (GetDetection, GetDecoratedItems,
      // GetTotals, GetKeystone, GetProtocolBreakage). Templates call these
      // many times per render — each `x-text`, `x-if`, `:class` re-evaluates
      // its expression. Without memoization, a single drawer render does 30+
      // redundant calculations. Cleared on every cart mutation via the
      // cart $watch handler below; safe to keep around within a single render
      // pass.
      _cache: {},

      /* ----- lifecycle ----- */
      init() {
        this.InitWatchers();
        this.InitEventHandlers();
        this.InitSelf();
        _runSelfTests();
      },

      InitSelf() {
        this.flags.preview_mode = this.$el.dataset.preview === 'true';
        this.UpdateCart();
        this.MaybeFetchFoundationProduct();
        if (this.flags.preview_mode) {
          this.flags.open = true;
        }
      },

      // One-time fetch on init. Empty-state foundation card binds to
      // foundation_product?.image / foundation_product?.sub_price.
      async MaybeFetchFoundationProduct() {
        if (this.foundation_product) return;
        try {
          const product = await this.FetchProduct('hormone-balance');
          // First sub variant + first selling plan, mirrors the bridge derive logic
          const variant = product.variants.find((v) => v.available && v.selling_plan_allocations && v.selling_plan_allocations.length) || product.variants[0];
          const plan = (variant && variant.selling_plan_allocations && variant.selling_plan_allocations[0]) || null;
          this.foundation_product = {
            handle: product.handle,
            url: product.url,
            image: product.featured_image,
            title: product.title,
            otp_price: variant ? variant.price : product.price,
            sub_price: plan ? plan.per_delivery_price : (variant ? variant.price : product.price)
          };
        } catch (e) {
          // Non-fatal — empty state falls back to the bottle silhouette + default text.
          console.warn('[v2] foundation product fetch failed', e);
        }
      },

      InitWatchers() {
        const am = this;
        am.$watch('flags.open', (open) => {
          document.body.classList.toggle('cv2-open', open);
        });
        am.$watch('cart', () => {
          // Drop memoization cache — cart contents changed, every derived
          // value (detection, decoration, totals, keystone, breakage) needs
          // to be recomputed against the new state.
          am._cache = {};
          am.flags.empty = (am.cart.item_count || 0) === 0;
          am.flags.protocol_active = !!am.GetDetection().primary;
          am.ReconcileProtocolDiscount();
          am.MaybeFetchBridgeProduct();
          am.MaybeFetchKeystonePartner();
          am.RecordFormedProtocols();
        });
      },

      InitEventHandlers() {
        const am = this;
        document.addEventListener('cart:drawer:open', () => { am.flags.open = true; });
        document.addEventListener('cart:drawer:close', () => { am.flags.open = false; });
        document.addEventListener('cart:modified', async () => {
          await am.UpdateCart();
        });
        document.querySelectorAll('#cart-icon-bubble').forEach((el) => {
          el.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            am.flags.open = true;
          });
        });
        if (window.UTILS && typeof window.UTILS.ObserveMutation === 'function') {
          window.UTILS.ObserveMutation('#cart-icon-bubble', {}, async () => {
            await am.UpdateCart();
          });
        }
      },

      /* ----- cart fetch / apply ----- */

      async UpdateCart() {
        const cart = await fetch('/cart.js').then((r) => r.json());
        this.ApplyCart(cart);
      },

      ApplyCart(cart) {
        const am = this;
        if (Array.isArray(cart.items)) {
          cart.items = [...cart.items].reverse(); // newest-on-top
        }
        am.$nextTick(() => {
          am.cart = { ...cart, attributes: cart.attributes || {} };
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        });
        document.querySelectorAll('[data-cart-count]').forEach(($el) => {
          $el.textContent = cart.item_count;
        });
      },

      /* ----- detection + decoration accessors (called from templates) -----
         All five accessors below are memoized against this._cache. The cache
         is cleared by the cart $watch on every cart mutation. Within a single
         render pass, repeated calls return the cached value — critical because
         Alpine re-evaluates every template binding (x-text, x-if, :class)
         whenever any reactive dependency changes, and the cart drawer has
         many bindings that reference these accessors. Without memoization,
         a typical drawer render does 30+ redundant detection passes. */

      GetDetection() {
        if (!this._cache.detection) {
          this._cache.detection = window.__CartV2.detectProtocols(this.cart.items || []);
        }
        return this._cache.detection;
      },

      GetDecoratedItems() {
        if (!this._cache.decorated) {
          const detection = this.GetDetection();
          this._cache.decorated = (this.cart.items || []).map((i) => window.__CartV2.decorateLineItem(i, detection));
        }
        return this._cache.decorated;
      },

      // Protocol breakage state. Reads sessionStorage for recently-formed
      // protocols and returns the breakage info (which protocol was formed,
      // which constituent is still in cart, which is missing) so the
      // keystone module can suggest re-completion instead of the default
      // partner mapping.
      // Memoized — `_breakage` key uses null-safe storage via `_breakage_set`
      // sentinel so cached null returns don't trigger recomputation.
      GetProtocolBreakage() {
        if (this._cache._breakage_set) return this._cache._breakage;

        const items = this.cart.items || [];
        const detection = this.GetDetection();
        const currentSlugs = detection.formed.map((f) => f.protocol.slug);

        let stored;
        try {
          stored = JSON.parse(sessionStorage.getItem(RECENT_PROTOCOL_KEY) || '[]');
        } catch (e) {
          stored = [];
        }

        // Find protocols that WERE formed but no longer are
        const missing = stored.filter((slug) => currentSlugs.indexOf(slug) === -1);
        if (missing.length === 0) {
          this._cache._breakage = null;
          this._cache._breakage_set = true;
          return null;
        }

        // For each missing protocol, check if at least one constituent
        // is still in cart. If so, the customer broke it by removing
        // the other constituent → we can suggest re-completion.
        for (const slug of missing) {
          const protocol = window.__CartV2.DEFINED_PROTOCOLS.find((p) => p.slug === slug);
          if (!protocol) continue;
          const aHandle = protocol.individuals[0];
          const bHandle = protocol.individuals[1];
          const aInCart = items.some((i) => i.handle === aHandle && window.__CartV2.isSubscription(i));
          const bInCart = items.some((i) => i.handle === bHandle && window.__CartV2.isSubscription(i));

          if (aInCart && !bInCart) {
            this._cache._breakage = { protocol: protocol, held_handle: aHandle, missing_handle: bHandle };
            this._cache._breakage_set = true;
            return this._cache._breakage;
          }
          if (bInCart && !aInCart) {
            this._cache._breakage = { protocol: protocol, held_handle: bHandle, missing_handle: aHandle };
            this._cache._breakage_set = true;
            return this._cache._breakage;
          }
        }
        this._cache._breakage = null;
        this._cache._breakage_set = true;
        return null;
      },

      // Persist current formed protocols to sessionStorage. Called from
      // the cart watcher. Keeps the list bounded (max ~5).
      RecordFormedProtocols() {
        const detection = this.GetDetection();
        const currentSlugs = detection.formed.map((f) => f.protocol.slug);

        let stored;
        try {
          stored = JSON.parse(sessionStorage.getItem(RECENT_PROTOCOL_KEY) || '[]');
        } catch (e) {
          stored = [];
        }
        // Merge: keep slugs that disappeared (we want to detect breakage),
        // and add any newly-formed ones. When a previously-broken slug
        // re-forms, drop it from the breakage memory.
        const next = Array.from(new Set([...stored, ...currentSlugs])).slice(-5);
        try {
          sessionStorage.setItem(RECENT_PROTOCOL_KEY, JSON.stringify(next));
        } catch (e) { /* sessionStorage unavailable, no-op */ }
      },

      // Memoized — keystone return may be null (no partner suggested) or a
      // partner object. Uses `_keystone_set` sentinel like GetProtocolBreakage
      // so cached nulls don't recompute.
      GetKeystone() {
        if (this._cache._keystone_set) return this._cache._keystone;

        const detection = this.GetDetection();
        // Breakage override: if a recently-formed protocol broke and one
        // of its constituents is still in the cart, suggest re-completing
        // THAT protocol instead of the default keystone partner.
        const breakage = this.GetProtocolBreakage();
        let result;
        if (breakage) {
          result = {
            held_item: (this.cart.items || []).find((i) => i.handle === breakage.held_handle) || null,
            partner_handle: breakage.missing_handle,
            protocol: breakage.protocol,
            from_breakage: true
          };
        } else {
          result = window.__CartV2.detectKeystone(this.cart.items || [], detection);
        }
        this._cache._keystone = result;
        this._cache._keystone_set = true;
        return result;
      },

      // Memoized — totals depends on detection + decoration + customer codes,
      // all of which derive from cart state, so a single cart-fingerprint
      // cache is sufficient.
      GetTotals() {
        if (!this._cache.totals) {
          const detection = this.GetDetection();
          const decorated = this.GetDecoratedItems();
          const customer_codes = this.GetCustomerCodes();
          this._cache.totals = window.__CartV2.computeTotals(decorated, detection, customer_codes);
        }
        return this._cache.totals;
      },

      GetPrimaryProtocol() {
        const d = this.GetDetection();
        return d.primary ? d.primary.protocol : null;
      },

      // Shipping qualification state. Drives both the top banner (confirmed
      // free vs progress-bar remaining) and the order summary's "Shipping"
      // row text.
      // Customer-applied promo codes (everything EXCEPT our Protocol_pricing
      // code, which is surfaced via the per-line role tag). Each entry has
      // { code, amount_cents } so the Order Summary can render a savings row.
      // Driven by a URL like /discount/WELCOME10, a promo banner, or a code
      // a customer typed at checkout that's been carried back to the cart.
      GetCustomerCodes() {
        const apps = this.cart.cart_level_discount_applications || [];
        const out = [];
        for (const d of apps) {
          if (!d) continue;
          const title = (d.title || d.code || '').trim();
          if (!title) continue;
          if (title.toLowerCase() === PROTOCOL_DISCOUNT_CODE.toLowerCase()) continue;
          // total_allocated_amount is a string like "740" (cents) on /cart.js
          const amount = parseInt(d.total_allocated_amount || 0, 10) || 0;
          out.push({ code: title, amount_cents: amount });
        }
        return out;
      },

      GetShippingState() {
        const items = this.cart.items || [];
        const hasSub = items.some((i) => !!i.selling_plan_allocation);
        const subtotal = this.cart.total_price || 0;
        const threshold = FREE_SHIP_THRESHOLD_CENTS;

        if (hasSub || subtotal >= threshold) {
          return {
            qualified: true,
            reason: hasSub ? 'sub_in_cart' : 'otp_threshold_met',
            threshold: threshold,
            subtotal: subtotal,
            remaining: 0,
            progress: 100
          };
        }
        const remaining = Math.max(0, threshold - subtotal);
        const progress = Math.max(0, Math.min(100, (subtotal / threshold) * 100));
        return {
          qualified: false,
          reason: 'otp_below_threshold',
          threshold: threshold,
          subtotal: subtotal,
          remaining: remaining,
          progress: progress
        };
      },

      // Items belonging to the primary protocol section (when a protocol is formed)
      GetProtocolItems() {
        const detection = this.GetDetection();
        if (!detection.primary) return [];
        const constituents = detection.primary.constituent_items || [];
        const keys = constituents.map((c) => c.key);
        return this.GetDecoratedItems().filter((i) => keys.indexOf(i.key) !== -1);
      },

      // Personalization items (rendered separately under the protocol section)
      GetPersonalizationItems() {
        const detection = this.GetDetection();
        if (!detection.primary) return [];
        const constituents = detection.primary.constituent_items || [];
        const keys = constituents.map((c) => c.key);
        return this.GetDecoratedItems().filter((i) => keys.indexOf(i.key) === -1);
      },

      // Bridge position-aware eyebrow: "Add the [Nth] layer — [thing]"
      GetBridgeEyebrow() {
        const protocol = this.GetPrimaryProtocol();
        if (!protocol) return '';
        const layer_n = (this.cart.item_count || 0) + 1;
        const ordinal = this.ToOrdinal(layer_n);
        return 'Add the ' + ordinal + ' layer, ' + protocol.bridge_eyebrow_layer;
      },

      ToOrdinal(n) {
        const map = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'fifth', 6: 'sixth', 7: 'seventh' };
        return map[n] || (n + 'th');
      },

      /* ----- bridge product fetch ----- */

      async MaybeFetchBridgeProduct() {
        const protocol = this.GetPrimaryProtocol();
        if (!protocol || !protocol.bridge_product_handle) {
          this.bridge_product = null;
          return;
        }
        // Don't surface a bridge whose product is already in the cart.
        const inCart = (this.cart.items || []).some((i) => i.handle === protocol.bridge_product_handle);
        if (inCart) {
          this.bridge_product = null;
          return;
        }
        // Already fetched?
        if (this.bridge_product && this.bridge_product.handle === protocol.bridge_product_handle) return;

        const handle = protocol.bridge_product_handle;
        window.__semaine_product_cache = window.__semaine_product_cache || {};
        if (!window.__semaine_product_cache[handle]) {
          window.__semaine_product_cache[handle] = fetch('/products/' + handle + '.js').then((r) => r.json());
        }
        const product = await window.__semaine_product_cache[handle];
        this.bridge_product = this.DeriveBridgeProductDisplay(product);
      },

      DeriveBridgeProductDisplay(product) {
        // Match the cart's dominant cadence so 3-pack carts see 3-pack
        // bridge pricing (not 1-pack pricing applied to 3-pack add).
        const cadence = this.GetCartCadence();
        const matched = this.MatchVariantToCadence(product, cadence);
        const variant = matched.variant || product.variants[0];
        const plan = matched.plan;
        const otp = variant ? variant.price : product.price;
        const sub_price = plan ? plan.per_delivery_price : otp;
        const sub_discount = otp > 0 ? Math.max(0, (otp - sub_price) / otp) : 0;
        // Multiplicative — same logic as the line-item decorator. Matches
        // Shopify's checkout discount stacking.
        const protocol_price = Math.round(sub_price * (1 - PROTOCOL_UPLIFT));

        // For daily cost framing: the variant's protocol_price represents
        // a delivery period that depends on pack size (1 Pack = ~30 days,
        // 3 Pack = ~90 days). Normalize to a per-day figure.
        const pack = (cadence && cadence.pack) || '1 Pack';
        const period_days = (pack === '3 Pack') ? 90 : (pack === '2 Pack' ? 60 : 30);
        const daily_cost_cents = protocol_price / period_days; // float

        return {
          handle: product.handle,
          title: product.title,
          image: product.featured_image,
          url: product.url,
          variant_id: variant ? variant.id : null,
          selling_plan_id: plan ? plan.selling_plan_id : null,
          otp_price: otp,
          sub_price: sub_price,
          protocol_price: protocol_price,
          sub_discount: sub_discount,
          pack: pack,
          period_days: period_days,
          daily_cost_cents: daily_cost_cents
        };
      },

      // ----- daily-cost framing for the keystone CTA -----
      // Lazy-fetch the partner product once detected, surface its
      // protocol-price-per-day so the CTA reads "{verb} · Just $X.YY/day more".
      keystone_partner: null,

      async MaybeFetchKeystonePartner() {
        const ks = this.GetKeystone();
        if (!ks) { this.keystone_partner = null; return; }
        if (this.keystone_partner && this.keystone_partner.handle === ks.partner_handle) return;
        const product = await this.FetchProduct(ks.partner_handle);
        this.keystone_partner = this.DeriveBridgeProductDisplay(product);
      },

      // Formatted "Just $X.YY/day more" — null when partner not yet loaded.
      GetKeystoneDailyTag() {
        if (!this.keystone_partner) return null;
        const cents = this.keystone_partner.daily_cost_cents;
        if (!isFinite(cents) || cents <= 0) return null;
        const dollars = cents / 100;
        return 'Just $' + dollars.toFixed(2) + '/day more';
      },

      GetBridgeDailyTag() {
        if (!this.bridge_product) return null;
        const cents = this.bridge_product.daily_cost_cents;
        if (!isFinite(cents) || cents <= 0) return null;
        const dollars = cents / 100;
        return 'Just $' + dollars.toFixed(2) + '/day to your subscription';
      },

      // Punchy short version for inline display next to the price.
      // "$0.86/day" reads as a value signal, not as a footnote contingency.
      GetBridgeDailyPriceShort() {
        if (!this.bridge_product) return null;
        const cents = this.bridge_product.daily_cost_cents;
        if (!isFinite(cents) || cents <= 0) return null;
        const dollars = cents / 100;
        return '$' + dollars.toFixed(2) + '/day';
      },

      /* ----- discount code reconciliation -----
         Full cycle:
           - Protocol formed → cart attribute = 'true', apply Protocol_pricing
             code if no other code is present (preserve customer-entered codes)
           - Protocol broken → cart attribute = '', clear Protocol_pricing
             code (leave any other customer-entered code alone)

         A Shopify Validation Function gates the checkout as a back-stop —
         if Protocol_pricing is applied but the cart doesn't contain a valid
         pair, the Function blocks. See extensions/protocol-pricing-guard/. */

      async ReconcileProtocolDiscount() {
        const am = this;
        const detection = am.GetDetection();
        const protocolFormed = !!detection.primary;
        const attrActive = am.cart.attributes && am.cart.attributes[PROTOCOL_CART_ATTR] === 'true';

        // 1. Sync the cart attribute (independent signal from the discount
        //    code — used by Layer 2/3 systems that read the cart attribute
        //    directly, e.g. analytics, ops dashboards).
        if (protocolFormed !== attrActive) {
          try {
            const body = {};
            body[PROTOCOL_CART_ATTR] = protocolFormed ? 'true' : '';
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: body })
            });
          } catch (e) {
            console.warn('[v2] cart attribute update failed', e);
          }
        }

        // 2. Read the cart's currently-applied discount codes. Use the
        //    cart.discount_codes array — cart_level_discount_applications
        //    is EMPTY for product/line-level discounts (PROTOCOL is one).
        //    cart.discount_codes is the canonical "what codes does the
        //    customer have applied" source regardless of allocation level.
        const applied = (am.cart.discount_codes || [])
          .filter(d => d && d.applicable)
          .map(d => d.code || '')
          .filter(Boolean);
        const ourCodeApplied = applied.some(c => c.toLowerCase() === PROTOCOL_DISCOUNT_CODE.toLowerCase());

        // Stack with whatever customer-entered codes are present.
        // Shopify's discount engine handles the stacking math at checkout
        // (the PROTOCOL code is configured in admin to combine with product/
        // order/shipping discounts), so we don't need to choose between
        // codes here — apply ours whenever a protocol is formed.
        if (protocolFormed && !ourCodeApplied) {
          try {
            await fetch('/discount/' + encodeURIComponent(PROTOCOL_DISCOUNT_CODE), { redirect: 'manual' });
          } catch (e) {
            // expected when code doesn't yet exist in Shopify admin; safe
          }
        } else if (!protocolFormed && ourCodeApplied) {
          // Clear OUR code when the protocol breaks. Shopify's AJAX cart
          // accepts `discount: ''` on /cart/update.js to clear ALL codes,
          // which could nuke a customer-entered code too. Mitigation:
          // re-apply any non-PROTOCOL code immediately after clearing.
          // (In practice Shopify usually allows only one cart-level code
          // at a time, but this guards against future multi-code stacking.)
          const otherCodes = applied.filter(c => c.toLowerCase() !== PROTOCOL_DISCOUNT_CODE.toLowerCase());
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: '' })
            });
            // Restore the customer's own code(s) if any
            for (const code of otherCodes) {
              try {
                await fetch('/discount/' + encodeURIComponent(code), { redirect: 'manual' });
              } catch (e) { /* per-code restore failure non-fatal */ }
            }
          } catch (e) {
            console.warn('[v2] protocol discount clear failed', e);
          }
        }
      },

      /* ----- mutation handlers ----- */

      async e_UpdateLineQty(item, newQty) {
        if (newQty < 0) newQty = 0;
        const updates = {};
        updates[item.key] = newQty;
        try {
          const cart = await (window.UTILS && window.UTILS.UpdateCartItems
            ? window.UTILS.UpdateCartItems(updates)
            : fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: updates })
              }).then((r) => r.json()));
          this.ApplyCart(cart);
        } catch (e) {
          console.error('[v2] qty update failed', e);
        }
      },

      async e_IncrementQty(item) {
        await this.e_UpdateLineQty(item, (item.quantity || 0) + 1);
      },

      async e_DecrementQty(item) {
        const next = (item.quantity || 0) - 1;
        await this.e_UpdateLineQty(item, next);
      },

      async e_RemoveItem(item) {
        await this.e_UpdateLineQty(item, 0);
      },

      // Convert an OTP line item to a subscription in place.
      // Uses /cart/change.js with selling_plan to swap the cadence without
      // changing the variant. Picks the first available selling plan for
      // that variant — Phase 1b can expose a cadence chooser.
      async e_ConvertToSubscription(item) {
        if (!item || !item.handle) return;
        try {
          const product = await this.FetchProduct(item.handle);
          const variant = product.variants.find((v) => v.id === item.variant_id) || product.variants.find((v) => v.option1 === item.variant_title);
          if (!variant || !variant.selling_plan_allocations || !variant.selling_plan_allocations.length) {
            console.warn('[v2] no sub plan available for', item.handle);
            return;
          }
          const plan = variant.selling_plan_allocations[0];
          const cart = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: item.quantity,
              selling_plan: plan.selling_plan_id
            })
          }).then((r) => r.json());
          this.ApplyCart(cart);
        } catch (e) {
          console.error('[v2] convert to sub failed', e);
        }
      },

      /* ----- cadence matching ----- */

      // Read the cart's dominant cadence so partner adds (keystone CTA,
      // bridge CTA) match. Falls back to '1 Pack' / first selling plan
      // if no subscription items exist.
      //
      // Returns: { pack: '1 Pack' | '3 Pack', plan_position: 0 | 1 | ... }
      GetCartCadence() {
        const subs = (this.cart.items || []).filter(window.__CartV2.isSubscription);
        if (subs.length === 0) return { pack: '1 Pack', plan_position: 0 };

        // Sort by added-at (proxy: original cart order before reverse).
        // The first sub item's pack size becomes the convention.
        const first = subs[0];

        // Pack size is variant option1, surfaced on the line item as
        // variant_title (the variant's display name) or options_with_values.
        let pack = first.variant_title || '1 Pack';
        // variant_title sometimes appears as "1 Pack" or just "1 Pack"
        // (no product prefix). Normalize.
        if (typeof pack === 'string') {
          if (pack.toLowerCase().includes('3 pack')) pack = '3 Pack';
          else if (pack.toLowerCase().includes('2 pack')) pack = '2 Pack';
          else pack = '1 Pack';
        }

        // Plan position — for v1, always use the first plan that matches
        // the pack size's cadence. Phase 1b can read the actual frequency.
        return { pack: pack, plan_position: 0 };
      },

      // Given a product and a cadence, return the matching variant +
      // its first selling plan allocation. Falls back to first available.
      MatchVariantToCadence(product, cadence) {
        if (!product || !product.variants) return { variant: null, plan: null };

        const wantPack = (cadence && cadence.pack) || '1 Pack';

        const matchByOption = product.variants.find(
          (v) => v.option1 === wantPack && v.available && v.selling_plan_allocations && v.selling_plan_allocations.length
        );
        const fallback = product.variants.find(
          (v) => v.available && v.selling_plan_allocations && v.selling_plan_allocations.length
        );
        const variant = matchByOption || fallback || product.variants[0];
        if (!variant) return { variant: null, plan: null };
        const planIdx = (cadence && cadence.plan_position) || 0;
        const plan = (variant.selling_plan_allocations && variant.selling_plan_allocations[planIdx])
          || (variant.selling_plan_allocations && variant.selling_plan_allocations[0])
          || null;
        return { variant: variant, plan: plan };
      },

      // Cached lazy fetcher used by both the keystone partner display +
      // bridge product. Returns the parsed product JSON.
      async FetchProduct(handle) {
        const cache = window.__semaine_product_cache = window.__semaine_product_cache || {};
        if (!cache[handle]) {
          cache[handle] = fetch('/products/' + handle + '.js').then((r) => r.json());
        }
        return cache[handle];
      },

      async e_CompleteProtocol(partner_handle) {
        // Match the cart's existing cadence so a 3-pack keystone holds
        // and the partner enters at 3-pack 3-month (not silent 1-pack).
        const cadence = this.GetCartCadence();
        const product = await this.FetchProduct(partner_handle);
        const { variant, plan } = this.MatchVariantToCadence(product, cadence);
        if (!variant) return;
        const payload = {
          items: [
            {
              id: variant.id,
              quantity: 1,
              selling_plan: plan ? plan.selling_plan_id : null
            }
          ]
        };
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          document.dispatchEvent(new CustomEvent('cart:modified'));
        } catch (e) {
          console.error('[v2] add partner failed', e);
        }
      },

      async e_AddBridge() {
        const protocol = this.GetPrimaryProtocol();
        if (!protocol || !protocol.bridge_product_handle) return;
        const cadence = this.GetCartCadence();
        const product = await this.FetchProduct(protocol.bridge_product_handle);
        const { variant, plan } = this.MatchVariantToCadence(product, cadence);
        if (!variant) return;
        const payload = {
          items: [
            {
              id: variant.id,
              quantity: 1,
              selling_plan: plan ? plan.selling_plan_id : null
            }
          ]
        };
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          document.dispatchEvent(new CustomEvent('cart:modified'));
        } catch (e) {
          console.error('[v2] bridge add failed', e);
        }
      },

      /* ----- formatting + actions ----- */

      FormatMoney(value) {
        if (window.UTILS && typeof window.UTILS.FormatMoney === 'function') {
          return window.UTILS.FormatMoney(value);
        }
        return '$' + ((value || 0) / 100).toFixed(2);
      },

      FormatMoneyShort(value) {
        // Used in CTAs / short price displays. Drops cents when whole-dollar.
        var v = (value || 0) / 100;
        if (v === Math.floor(v)) return '$' + v.toFixed(0);
        return '$' + v.toFixed(2);
      },

      // Convert per-month-savings into a "$X.YY/day more" framing for the
      // keystone CTA tag. Returns formatted string.
      DailyCost(monthly_cents) {
        var per_day = (monthly_cents / 100) / 30;
        return '$' + per_day.toFixed(2);
      },

      e_Close() {
        this.flags.open = false;
      },

      e_ContinueShopping() {
        this.flags.open = false;
      },

      e_Checkout() {
        window.location.href = '/checkout';
      }
    }));
  });
})();
